import { useAxiosInstance } from "@iloq/shared-react/use-axios-instance.hook";
import { LeadForwardingProject } from "@iloq/shared/index";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { ProjectUiStatus, ProjectUiStatusType } from "./project-ui-status";
import { PROJECTS_QUERY_KEY } from "./projects-query-key";

export function useProjectsQuery(currency: string, isDisabled?: boolean) {
  const axiosInstance = useAxiosInstance();

  return useQuery({
    queryKey: [PROJECTS_QUERY_KEY, currency, isDisabled],
    queryFn: async () => {
      if (isDisabled) {
        return null;
      }
      const response = await axiosInstance.get<LeadForwardingProject[]>(
        `/api/v2/partner-program/opportunities?currency=${currency}`,
      );
      if (response.status !== 200) {
        throw new Error(
          `Failed to list opportunity projects: ${response.status} - ${response.statusText}`,
        );
      }
      return response.data.map((project) =>
        Object.assign({}, project, {
          uiStatus: resolveUiStatus(project),
        }),
      );
    },
    staleTime: 1000 * 60 * 30, // 30 minutes cache
  });
}

function resolveUiStatus({
  projectStatus,
  reviewStatus,
  lastOrderingDate,
}: LeadForwardingProject): ProjectUiStatusType {
  if (reviewStatus === "DECLINED") {
    return ProjectUiStatus.DECLINED;
  }
  if (lastOrderingDate && DateTime.fromISO(lastOrderingDate) < DateTime.now()) {
    return ProjectUiStatus.EXPIRED;
  }
  if (projectStatus === "WON" || projectStatus === "LOST") {
    return ProjectUiStatus.CLOSED;
  }
  if (reviewStatus === "IN_REVIEW") {
    return ProjectUiStatus.IN_REVIEW;
  }
  return ProjectUiStatus.APPROVED;
}
